/**
 * fontStack — the CSS `font-family` value for a brand font name.
 *
 * Brand fonts are stored as one family name ("Inter", "Lora"), and nothing
 * loads them as web fonts yet. A bare name the browser doesn't have falls back
 * to its default, which is serif on most systems. Emit a stack instead, so an
 * unavailable font falls back to the generic family of its own kind.
 *
 * Applied where tokens are emitted (resolveTokens, the Duro brand override),
 * never in generate(): the stored tokens stay the brand's own name.
 */

const GENERIC = new Set([
  "serif",
  "sans-serif",
  "monospace",
  "cursive",
  "fantasy",
  "system-ui",
  "ui-serif",
  "ui-sans-serif",
  "ui-monospace",
  "ui-rounded",
]);

/** Brand font choices whose fallback is a serif, not a sans. */
const SERIF = new Set(["Lora", "Playfair Display", "Georgia"]);

const FALLBACK = {
  sans: "system-ui, sans-serif",
  serif: "Georgia, serif",
  mono: "ui-monospace, monospace",
} as const;

/**
 * `kind` picks the fallback for a family it doesn't know: body and heading
 * fonts fall back to a sans (or a serif for the serif choices above), a mono
 * slot to a monospace.
 */
export function fontStack(
  name: string,
  kind: "text" | "mono" = "text",
): string {
  const trimmed = name.trim();
  // Already a stack, or a generic family the browser always has.
  if (trimmed.includes(",") || GENERIC.has(trimmed)) return trimmed;
  const bare = trimmed.replace(/^(["'])(.*)\1$/, "$2");
  const family = /\s/.test(bare) ? `"${bare}"` : bare;
  const fallback =
    kind === "mono"
      ? FALLBACK.mono
      : SERIF.has(bare)
        ? FALLBACK.serif
        : FALLBACK.sans;
  return `${family}, ${fallback}`;
}
